import { call, put } from "redux-saga/effects";
import {
    createExamination,
    getExamination,
    getExaminationById,
    startExamination,
    pauseExamination,
    completeExamination,
    getExaminationQuestions,
    submitExaminationAnswer,
    getExaminationTimeStatus,
    createExaminationAndNavigate,
    loadQuestionById
} from "../actions/examinationActionsHelper";
import * as examinationApi from '../../api/index';

// Get examinations saga
export function* handleGetExaminations(action) {
    try {
        // Note: Component already dispatched getExamination.request()
        const response = yield call(examinationApi.getExaminations);
        yield put(getExamination.success(response?.data));
    } catch (error) {
        yield put(getExamination.failed(error.response?.data?.message || error.message || 'Failed to fetch examinations.'));
    }
}

// Create examination saga
export function* handleCreateExamination(action) {
    try {
        console.log('🔄 Saga: Starting createExamination with payload:', action.payload);
        // Note: Don't dispatch request() here - the component already did that

        console.log('📡 Saga: Making API call to create examination');
        const response = yield call(examinationApi.createExamination, action.payload);
        console.log('✅ Saga: Full API response:', response);
        console.log('✅ Saga: Response data:', response?.data);

        // Extract examination data if nested
        const responseData = response?.data;
        const examinationData = responseData?.data?.examination || responseData?.examination || responseData;
        console.log('📋 Saga: Extracted examination data to dispatch:', examinationData);

        yield put(createExamination.success(responseData));
        console.log('🎉 Saga: createExamination.success dispatched with:', responseData);
    } catch (error) {
        console.error('❌ Saga: createExamination failed:', error);

        // Enhanced error handling for common issues
        let errorMessage = 'Failed to create examination.';

        if (error.response?.data?.message) {
            errorMessage = error.response.data.message;
        } else if (error.message) {
            // Handle specific error types
            if (error.message.includes('Cast to ObjectId failed')) {
                errorMessage = 'User ID format error. Please try logging in again.';
            } else if (error.message.includes('validation failed')) {
                errorMessage = 'Invalid examination data. Please check your input.';
            } else if (error.message.includes('Network Error')) {
                errorMessage = 'Network connection error. Please check your internet connection.';
            } else {
                errorMessage = error.message;
            }
        }

        console.error('🚨 Final error message:', errorMessage);
        yield put(createExamination.failed(errorMessage));
    }
}

// Get examination by ID saga
export function* handleGetExaminationById(action) {
    try {
        console.log('🔄 Saga: Getting examination by ID:', action.payload);
        // Note: Component already dispatched getExaminationById.request()
        const response = yield call(examinationApi.getExaminationById, action.payload);
        console.log('✅ Saga: Got examination response:', response?.data);
        yield put(getExaminationById.success(response?.data));
    } catch (error) {
        console.error('❌ Saga: Failed to get examination:', error);
        yield put(getExaminationById.failed(error.response?.data?.message || error.message || 'Failed to fetch examination.'));
    }
}

// Start examination saga
export function* handleStartExamination(action) {
    try {
        // Note: Component already dispatched startExamination.request()
        const response = yield call(examinationApi.startExamination, action.payload);
        yield put(startExamination.success(response?.data));
    } catch (error) {
        yield put(startExamination.failed(error.response?.data?.message || error.message || 'Failed to start examination.'));
    }
}

// Pause examination saga
export function* handlePauseExamination(action) {
    try {
        // Note: Component already dispatched pauseExamination.request()
        const response = yield call(examinationApi.pauseExamination, action.payload);
        yield put(pauseExamination.success(response?.data));
    } catch (error) {
        yield put(pauseExamination.failed(error.response?.data?.message || error.message || 'Failed to pause examination.'));
    }
}

// Complete examination saga
export function* handleCompleteExamination(action) {
    try {
        // Note: Component already dispatched completeExamination.request()
        const response = yield call(examinationApi.completeExamination, action.payload);
        yield put(completeExamination.success(response?.data));
    } catch (error) {
        yield put(completeExamination.failed(error.response?.data?.message || error.message || 'Failed to complete examination.'));
    }
}

// Get examination questions saga
export function* handleGetExaminationQuestions(action) {
    try {
        // Note: Component already dispatched getExaminationQuestions.request()
        const { examinationId, currentQuestionIndex, preloadCount = 3 } = action.payload;
        const params = {
            currentQuestionIndex: currentQuestionIndex.toString(),
            preloadCount: preloadCount.toString()
        };

        const response = yield call(examinationApi.getExaminationQuestions, examinationId, params);
        yield put(getExaminationQuestions.success(response?.data));
    } catch (error) {
        yield put(getExaminationQuestions.failed(error.response?.data?.message || error.message || 'Failed to load questions.'));
    }
}

// Submit examination answer saga
export function* handleSubmitExaminationAnswer(action) {
    try {
        // Note: Component already dispatched submitExaminationAnswer.request()
        const { examinationId, ...answerData } = action.payload;

        const response = yield call(examinationApi.submitExaminationAnswer, examinationId, answerData);
        yield put(submitExaminationAnswer.success(response?.data));
    } catch (error) {
        yield put(submitExaminationAnswer.failed(error.response?.data?.message || error.message || 'Failed to submit answer.'));
    }
}

// Get examination time status saga
export function* handleGetExaminationTimeStatus(action) {
    try {
        // Note: Component already dispatched getExaminationTimeStatus.request()
        const response = yield call(examinationApi.getExaminationTimeStatus, action.payload);
        yield put(getExaminationTimeStatus.success(response?.data));
    } catch (error) {
        yield put(getExaminationTimeStatus.failed(error.response?.data?.message || error.message || 'Failed to get time status.'));
    }
}

// Load individual question saga (lightweight approach)
export function* handleLoadQuestionById(action) {
    try {
        // Note: Component already dispatched loadQuestionById.request()
        const questionId = action.payload;
        const response = yield call(examinationApi.getQuestionById, questionId);

        yield put(loadQuestionById.success({
            questionId,
            questionData: response?.data
        }));
    } catch (error) {
        yield put(loadQuestionById.failed(error.response?.data?.message || error.message || 'Failed to load question.'));
    }
}

// Create examination and navigate saga
export function* handleCreateExaminationAndNavigate(action) {
    try {
        // Note: Component already dispatched createExaminationAndNavigate.request()
        const { examinationData, navigate } = action.payload;

        const response = yield call(examinationApi.createExamination, examinationData);
        const createdExamination = response?.data;

        // Navigate to the examination page
        if (navigate && createdExamination?._id) {
            navigate(`/user-full-test/${createdExamination._id}`);
        }

        yield put(createExaminationAndNavigate.success(createdExamination));
    } catch (error) {
        yield put(createExaminationAndNavigate.failed(error.response?.data?.message || error.message || 'Failed to create examination.'));
    }
}